import { sortedIndex, sortedLastIndex } from "lodash"
import { PageType } from "../Types/PageType"
import { Page } from "./Page"
import { CompositeTimeSeriesData, TimeSeriesData } from "./TimeSeriesData"
import { EMRData, EMRDataStream, EMRDataStreamId } from "../../../../../Managers/VisualizationManager/ToolBar/Modals/MedicationGraphModalities/Medications"
import { SocketResponse } from "../Types/SocketResponse"
import { ModalityPageManager } from "./ModalityPageManager"
import { ModalityDataSource } from "../Types/ModalityDataSource"
import { OrderedBuffer } from "./OrderedBuffer"


export class ModalityPage extends Page<TimeSeriesData | CompositeTimeSeriesData> {
    populateEMRData(optionalModalityFilter?: string): void {
        this.modalityDataSources
            .filter(dataSource => dataSource.isEMRData && (!optionalModalityFilter || dataSource.modality === optionalModalityFilter))
            .forEach(dataSource => {
                const responseBuffer = this.setUpEMRDataResponseBuffer(dataSource)
                const emrTimeSeriesData = this.getEMRTimeSeriesData(dataSource)
                const emrSocketResponse = this.getEMRDataSocketResponse(dataSource)
                const { dataObjectDataMap, timingInformationMap } = this.setUpDataStorage(emrSocketResponse)

                dataObjectDataMap.set(dataSource.modality, emrTimeSeriesData)

                timingInformationMap.set(dataSource.modality, {
                    gapIndexes: [],
                    samplingPeriod: Infinity, // There is no regular sampling period for this kind of data. Set it to the page size.
                })

                // For EMR data, set the unloaded time as the page end time for perceived continuity (due to low update frequency).
                responseBuffer.setMaxId(1)
                responseBuffer.add(1, emrSocketResponse)
                responseBuffer.updateUnloadedStartTime(this.endTime)

                this.invalidateModalityRenderCache(dataSource.modality, true, true)
            })

        setTimeout(() => {
            this.onPartialLoad?.(this)
        }, 500)
    }

    private setUpEMRDataResponseBuffer = (dataSource: ModalityDataSource): OrderedBuffer<SocketResponse> => {
        const buffer = new OrderedBuffer<SocketResponse>()
        this.responseBuffers.set(dataSource.modality, buffer)
        return buffer
    }

    private getEMRDataSocketResponse = (dataSource: ModalityDataSource): SocketResponse => {
        const startTimeMicroseconds = this.startTime * 1000
        const endTimeMicroseconds = Math.min(this.endTime, this.maxReadTime ?? Infinity) * 1000

        return {
            request_id: "EMR Data",
            page_id: this.id,
            data_object_id: dataSource.dataObjectId,
            pixels: new ArrayBuffer(0),
            conversion_factor: 1,
            order: 1,
            total: 1,
            modality: dataSource.modality,
            start_time: startTimeMicroseconds,
            end_time: endTimeMicroseconds,
            data_end_time: endTimeMicroseconds,
            chunk_end_time: endTimeMicroseconds,
            is_timestamp_compression_enabled: false,
        }
    }


    private getEMRTimeSeriesData = (dataSource: ModalityDataSource): TimeSeriesData => {
        const { id, code, units } = JSON.parse(dataSource.modality) as EMRDataStreamId
        const harmonized = (this.pageManager as ModalityPageManager).getEMRData().find((emrData: EMRData) => emrData.id === id)

        const emptyTimeSeriesData: TimeSeriesData = {
            data: new Float32Array(),
            times: []
        }

        if (!harmonized) {
            return emptyTimeSeriesData
        }

        const dataStream = harmonized.dataStreams.find((dataStream: EMRDataStream) => dataStream.code === code && dataStream.units === units)

        if (!dataStream) {
            return emptyTimeSeriesData
        }

        // Get only the slice of data that this page is responsible for
        const startIndex = sortedIndex(dataStream.times, this.startTime)
        const endIndex = sortedLastIndex(dataStream.times, this.endTime)

        const dataSlice = dataStream.data.slice(startIndex, endIndex)
        const timeSlice = dataStream.times.slice(startIndex, endIndex)

        // Always extend the last point of an infusion with the last point that is before the current end time of the data.
        if (dataStream.isContinuous && dataStream.data.length > 0) {
            const extendIndex = endIndex - 1

            if (extendIndex >= 0) {
                // If applicable, add a start point to prevent weird rendering problems when there is only one point per page.
                if (dataStream.times[extendIndex] < this.startTime) {
                    dataSlice.push(dataStream.data[extendIndex])
                    timeSlice.push(this.startTime)
                }

                // Add a data point at the end of the page.
                dataSlice.push(dataStream.data[extendIndex])
                timeSlice.push(this.endTime)
            }
        }

        return {
            data: new Float32Array(dataSlice),
            times: timeSlice
        }
    }

    cleanUpRenderedData(renderedData: ImageBitmap): void {
        renderedData.close()
    }

    getType(): PageType {
        return PageType.MODALITY
    }

}